import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { MdWarning, MdNightsStay } from 'react-icons/md';
import ModalPernoite from '../components/modal-pernoite';
import { FaUnlock, FaBan, FaPlay, FaHistory } from 'react-icons/fa';
import styled from 'styled-components';
import Listagem from '../../../components/listagem';
import { confirm } from '../../../components/confirm';
import { AcaoGrid } from '../../../services/tipos';
import { usuarioTemPermissao } from '../../../services/seguranca';
import { useAppSelector } from '../../../store/hooks';
import {
    OcorrenciaAtiva,
    listarOcorrenciasAtivas,
    impedirOcorrencia,
    reativarOcorrencia,
    desbloquearManual,
} from '../api';

const TIPO_PERNOITE = 6;

// Paleta alinhada ao badge de bloqueio da tela de veículos.
const SITUACAO: Record<string, { cor: string; texto: (o: OcorrenciaAtiva) => string }> = {
    bloqueado: { cor: '#f44336', texto: () => 'Bloqueado' },
    enviado: { cor: '#ff9800', texto: () => 'Bloqueio enviado' },
    avisando: { cor: '#fbc02d', texto: (o) => (o.totalAvisos > 0 ? `Avisando (${o.avisosEnviados}/${o.totalAvisos})` : 'Alertado') },
    detectada: { cor: '#42a5f5', texto: () => 'Detectado' },
    impedido: {
        cor: '#9c27b0',
        texto: (o) => o.impedidoAte
            ? `Impedido até ${new Date(o.impedidoAte).toLocaleDateString('pt-BR')}`
            : 'Impedido',
    },
};

const badgeSituacao = (o: OcorrenciaAtiva) => {
    const cfg = SITUACAO[o.situacao] ?? SITUACAO.detectada;
    return (
        <span style={{
            backgroundColor: cfg.cor, color: '#fff', padding: '3px 10px',
            borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
        }}>
            {cfg.texto(o)}
        </span>
    );
};

// ── Modal do "Impedir" ──
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center; z-index: 1300;
`;
const Box = styled.div`
  background: #fff; border-radius: 8px; padding: 24px; width: 420px; max-width: 92vw;
  box-shadow: 0 8px 30px rgba(0,0,0,.2);
`;
const Linha = styled.label`
  display: flex; align-items: center; gap: 8px; margin: 10px 0; cursor: pointer; font-size: 14px;
`;
const Botao = styled.button<{ $primario?: boolean }>`
  padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 14px;
  border: ${({ $primario }) => ($primario ? 'none' : '1px solid #ccc')};
  background: ${({ $primario }) => ($primario ? '#9c27b0' : '#fff')};
  color: ${({ $primario }) => ($primario ? '#fff' : '#333')};
`;

interface Props {
    idEmpresa: string;
    onVerHistorico: (idVeiculo: string, placa: string) => void;
}

const OcorrenciasTab: React.FC<Props> = ({ idEmpresa, onVerHistorico }) => {
    const token = useAppSelector(s => s.token);
    const podeEditar = usuarioTemPermissao(token, 'gestao_risco_editar');

    const [itens, setItens] = useState<OcorrenciaAtiva[]>([]);
    const [carregando, setCarregando] = useState(false);

    const [modalImpedir, setModalImpedir] = useState<OcorrenciaAtiva | null>(null);
    const [tipoImpedir, setTipoImpedir] = useState<'indef' | 'data'>('indef');
    const [dataImpedir, setDataImpedir] = useState('');
    const [pernoiteAberto, setPernoiteAberto] = useState<string | null>(null);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            // O DataGrid exige `id` único por linha — usamos o id da ocorrência.
            const dados = await listarOcorrenciasAtivas(idEmpresa);
            setItens(dados.map(o => ({ ...o, id: o.idOcorrencia })));
        } catch {
            toast.error('Erro ao carregar ocorrências.');
        } finally {
            setCarregando(false);
        }
    }, [idEmpresa]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregar(); }, []);

    const semPermissao = () => toast.error('Sem permissão para agir na Gestão de Risco.');

    const handleDesbloquear = useCallback(async (o: OcorrenciaAtiva) => {
        if (!podeEditar) return semPermissao();
        const ok = await confirm(
            `Desbloquear ${o.placa} agora? Se a condição do evento persistir, ele será bloqueado novamente no próximo ciclo. Para segurá-lo, use "Impedir".`
        );
        if (!ok) return;
        try {
            await desbloquearManual(idEmpresa, o.idVeiculo);
            toast.success('Comando de desbloqueio enviado.');
            carregar();
        } catch {
            toast.error('Erro ao desbloquear.');
        }
    }, [podeEditar, idEmpresa, carregar]);

    const handleReativar = useCallback(async (o: OcorrenciaAtiva) => {
        if (!podeEditar) return semPermissao();
        try {
            await reativarOcorrencia(o.idOcorrencia, idEmpresa);
            toast.success('Monitoramento reativado.');
            carregar();
        } catch {
            toast.error('Erro ao reativar.');
        }
    }, [podeEditar, idEmpresa, carregar]);

    const abrirImpedir = (o: OcorrenciaAtiva) => {
        if (!podeEditar) return semPermissao();
        setTipoImpedir('indef');
        setDataImpedir('');
        setModalImpedir(o);
    };

    const confirmarImpedir = useCallback(async () => {
        if (!modalImpedir) return;
        if (tipoImpedir === 'data' && !dataImpedir) {
            toast.error('Informe a data.');
            return;
        }
        const impedidoAte = tipoImpedir === 'data' ? `${dataImpedir}T23:59:59` : null;
        try {
            await impedirOcorrencia(modalImpedir.idOcorrencia, idEmpresa, impedidoAte);
            toast.success('Bloqueio impedido.');
            setModalImpedir(null);
            carregar();
        } catch {
            toast.error('Erro ao impedir.');
        }
    }, [modalImpedir, tipoImpedir, dataImpedir, idEmpresa, carregar]);

    const colunas = [
        { field: 'placa', headerName: 'Placa', width: 120 },
        { field: 'nomeEvento', headerName: 'Evento', flex: 1 },
        {
            field: 'situacao', headerName: 'Situação', width: 190,
            renderCell: (p: any) => badgeSituacao(p.row as OcorrenciaAtiva),
        },
        {
            field: 'ultimoAcionamentoData', headerName: 'Último acionamento', flex: 1,
            renderCell: (p: any) => {
                const o = p.row as OcorrenciaAtiva;
                if (!o.ultimoAcionamentoMsg) return '—';
                const quando = o.ultimoAcionamentoData
                    ? new Date(o.ultimoAcionamentoData).toLocaleString('pt-BR')
                    : '';
                return (
                    <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                        <span>{o.ultimoAcionamentoMsg}</span>
                        <span style={{ fontSize: 11, color: '#888' }}>{quando}</span>
                    </span>
                );
            },
        },
    ];

    const podeDesbloquear = (o: OcorrenciaAtiva) => o?.situacao === 'bloqueado' || o?.situacao === 'enviado';
    // Tipos que enviam bloqueio (inadimplência e alerta+bloqueio de distanciamento);
    // alerta-only (inatividade, distanciamento) não expõe ações de bloqueio.
    const TIPOS_BLOQUEIO = [1, 5];
    const ehBloqueante = (o: OcorrenciaAtiva) => TIPOS_BLOQUEIO.includes(o?.tipoEvento);

    const acoesExtras: AcaoGrid[] = [
        {
            Texto: 'Desbloquear agora', Icone: FaUnlock, Cor: '#f57c00',
            OnClick: handleDesbloquear,
            Condicao: (o: OcorrenciaAtiva) => ehBloqueante(o) && podeDesbloquear(o),
            TooltipDisabled: 'Só quando bloqueado ou com bloqueio enviado',
        },
        {
            Texto: 'Impedir bloqueio', Icone: FaBan, Cor: '#9c27b0',
            OnClick: abrirImpedir,
            Condicao: (o: OcorrenciaAtiva) => ehBloqueante(o) && o?.situacao !== 'impedido',
            TooltipDisabled: 'Já está impedido',
        },
        {
            Texto: 'Reativar monitoramento', Icone: FaPlay, Cor: '#2e7d32',
            OnClick: handleReativar,
            Condicao: (o: OcorrenciaAtiva) => ehBloqueante(o) && o?.situacao === 'impedido',
            TooltipDisabled: 'Só para veículos impedidos',
        },
        {
            Texto: 'Ver noites', Icone: MdNightsStay, Cor: '#1F467E',
            OnClick: (o: OcorrenciaAtiva) => setPernoiteAberto(o.idOcorrencia),
            Condicao: (o: OcorrenciaAtiva) => o?.tipoEvento === TIPO_PERNOITE,
            TooltipDisabled: 'Só para Monitoramento de Endereço',
        },
        {
            Texto: 'Ver histórico', Icone: FaHistory,
            OnClick: (o: OcorrenciaAtiva) => onVerHistorico(o.idVeiculo, o.placa),
        },
    ];

    return (
        <>
            <Listagem
                carregando={carregando}
                linhas={itens}
                colunas={colunas}
                nomeEntidade="Ocorrência"
                nomePropriedade="placa"
                exibirAcoesDeCrud={false}
                ocultarNovo={true}
                acoesExtras={acoesExtras}
                handleCriar={() => {}}
                handleSelecionarEntidade={() => {}}
                IconeEntidade={MdWarning}
            />

            <ModalPernoite idOcorrencia={pernoiteAberto} idEmpresa={idEmpresa} onClose={() => setPernoiteAberto(null)} />

            {modalImpedir && (
                <Overlay onClick={() => setModalImpedir(null)}>
                    <Box onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 4px' }}>Impedir bloqueio — {modalImpedir.placa}</h3>
                        <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
                            O veículo não será bloqueado (e será desbloqueado se estiver bloqueado) até você reativar.
                        </p>
                        <Linha>
                            <input type="radio" checked={tipoImpedir === 'indef'} onChange={() => setTipoImpedir('indef')} />
                            Até eu reativar
                        </Linha>
                        <Linha>
                            <input type="radio" checked={tipoImpedir === 'data'} onChange={() => setTipoImpedir('data')} />
                            Até a data:
                            <input
                                type="date"
                                value={dataImpedir}
                                onChange={(e) => { setDataImpedir(e.target.value); setTipoImpedir('data'); }}
                                style={{ padding: 4 }}
                            />
                        </Linha>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                            <Botao onClick={() => setModalImpedir(null)}>Cancelar</Botao>
                            <Botao $primario onClick={confirmarImpedir}>Impedir</Botao>
                        </div>
                    </Box>
                </Overlay>
            )}
        </>
    );
};

export default OcorrenciasTab;
